import { Injectable, Inject } from '@angular/core';
import { Router } from '@angular/router';
import { OktaAuth, AuthState } from '@okta/okta-auth-js';
import { OKTA_AUTH } from '@okta/okta-angular';
import { Observable, BehaviorSubject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { tap } from 'rxjs/operators';

export interface UserData {
  id: string;
  email: string;
  userName: string;
  firstName: string;
  lastName: string;
  company: string;
  city: string;
  stateId: number;
  stateName: string;
  dealerTypeId: number;
  phoneNumber: string;
  isActive: boolean;
  createDate: Date;
  roles: string[];
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private userSubject = new BehaviorSubject<UserData | undefined>(undefined);
  public currentUserO = this.userSubject.asObservable();
  public currentUser?: UserData;

  constructor(
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
    private router: Router,
    private http: HttpClient,
  ) {
    this.initializeAuth();
  }

  /**
   * Initialize authentication state
   */
  private async initializeAuth() {
    this.oktaAuth.authStateManager.subscribe((authState: AuthState) => {
      if (authState.isAuthenticated && !this.currentUser) {
        this.loadCurrentUser();
      } else if (!authState.isAuthenticated) {
        this.currentUser = undefined;
        this.userSubject.next(undefined);
      }
    });

    const isAuthenticated = await this.isAuthenticated();
    if (isAuthenticated) {
      this.loadCurrentUser();
    }
  }

  /**
   * Load current user from backend
   */
  private loadCurrentUser() {
    this.getCurrentUser().subscribe({
      next: (user: UserData) => {
        this.currentUser = user;
        this.userSubject.next(user);
      },
      error: (error) => {
        console.error('Failed to load user:', error);
      },
    });
  }

  /**
   * Redirect user to Okta login page
   */
  async login(): Promise<void> {
    await this.oktaAuth.signInWithRedirect({
      prompt: 'login',
      extraParams: { brand: 'fnbo' },
    });
  }

  /**
   * Sign out user and clear tokens
   */
  async logout(): Promise<void> {
    this.currentUser = undefined;
    this.userSubject.next(undefined);
    const idToken = this.oktaAuth.tokenManager.getTokensSync().idToken;
    localStorage.clear();
    await this.oktaAuth.signOut({
      idToken,
      postLogoutRedirectUri: window.location.origin,
    });
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    return await this.oktaAuth.isAuthenticated();
  }

  async getAccessToken(): Promise<string | undefined> {
    return this.oktaAuth.getAccessToken();
  }

  async getIdToken(): Promise<string | undefined> {
    return this.oktaAuth.getIdToken();
  }

  async getUserClaims(): Promise<any> {
    return this.oktaAuth.getUser();
  }

  /**
   * Get current user from backend (three-tier lookup)
   */
  getCurrentUser(): Observable<UserData> {
    return this.http.get<UserData>(`${environment.apiUrl}/identity/user`).pipe(
      tap((user: UserData) => {
        this.currentUser = user;
        this.userSubject.next(user);
      }),
    );
  }

  /**
   * Check if user is DFS Oversight
   */
  isDfsOversight(): boolean {
    return this.currentUser?.roles?.includes('DfsOversight') ?? false;
  }

  isDfsAdmin(): boolean {
    return this.currentUser?.roles?.includes('DfsAdmin') ?? false;
  }

  getCurrentUserRole(): string | undefined {
    return this.currentUser?.roles?.[0];
  }

  async hasRole(role: string): Promise<boolean> {
    if (this.currentUser?.roles) {
      return this.currentUser.roles.includes(role);
    }
    const user = await this.getUserClaims();
    return (user?.groups || []).includes(role);
  }

  async hasAnyRole(roles: string[]): Promise<boolean> {
    if (this.currentUser?.roles) {
      return roles.some((role) => this.currentUser!.roles.includes(role));
    }
    const user = await this.getUserClaims();
    return roles.some((role) => (user?.groups || []).includes(role));
  }

  authStateChanged(): Observable<AuthState> {
    return new Observable((observer) => {
      this.oktaAuth.authStateManager.subscribe((authState: AuthState) => {
        observer.next(authState);
      });
    });
  }

  async handleAuthentication(): Promise<void> {
    if (this.oktaAuth.isLoginRedirect()) {
      await this.oktaAuth.handleLoginRedirect();
      this.loadCurrentUser();
    }
  }

  checkUserIdleTimeout(): void {
    // TODO: Implement idle timeout logic if needed with Okta
    // Okta handles token refresh automatically
  }

  timeoutId?: any;

  /**
   * Get roles from backend (for admin user management)
   */
  getRoles(): Observable<any[]> {
    return this.http.get<any[]>(`${environment.apiUrl}/identity/Roles`);
  }

  /**
   * Update user role (for admin user management)
   */
  updateRole(editRole: any): Observable<any> {
    return this.http.post(`${environment.apiUrl}/identity/UserRole`, editRole);
  }

  /**
   * Register new user (for registration page)
   */
  register(data: any): Observable<any> {
    const registration = {
      userName: data.email,
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      company: data.company,
      city: data.city,
      stateId: data.stateId,
      phoneNumber: data.phone,
      dealerTypeId: data.dealerTypeId,
    };
    return this.http.post(
      `${environment.apiUrl}/identity/register`,
      registration,
    );
  }

  /**
   * Read JWT token (for reset password - placeholder, may not be needed with Okta)
   */
  readToken(token: string): any {
    try {
      return JSON.parse(atob(token.split('.')[1]));
    } catch (e) {
      return null;
    }
  }

  /**
   * Reset password (for forgot password flow)
   */
  resetPassword(password: string, token: string): Observable<any> {
    const data = { password: password, token: token };
    return this.http.post(
      `${environment.apiUrl}/identity/reset-password`,
      data,
    );
  }

  /**
   * Refresh token - Okta handles this automatically
   */
  refreshToken(): void {
    // Okta SDK handles token refresh automatically
    // This is a no-op for compatibility
  }
}
